import React, { useState } from 'react';
import { TooltipProvider } from 'components/ui/tooltip';
import { SidebarHeader } from './SidebarHeader';
import { SidebarCompanySelector } from './SidebarCompanySelector';
import { SidebarPlanSelector } from './SidebarPlanSelector';
import { SidebarNav } from './SidebarNav';
import { SidebarUserMenu } from './SidebarUserMenu';
import { AdminAccessSummary, AdminCompanySummary, PlanStatus, PlanSummary } from './planTypes';
import type { NavItemData } from './sidebarNavItems';
import type { ControlDelMenu, ControlDeNavegacion } from './navControl';
import { AccionesDeCuenta, conTextos, TextosDeNavegacion } from './navTextos';

/**
 * Recreación ESTÁTICA del sidebar real de la rama sin mergear
 * feature/resumen-multi-plan (src/components/Navigation/DesktopSidebarV2.tsx).
 * No importa el componente real: depende de props de Dashboard.tsx (tabs, rutas,
 * callbacks de navegación) que no tiene sentido cablear aquí. Reproduce su
 * estructura, sus clases reales (gris/blanco: es chrome compartido con Admin, por eso
 * no usa el verde de marca) y su copy real
 * (user.dashboard.myBenefit, etc. de ES.json).
 *
 * Este fichero es SOLO la composición: cada pieza (Header, PlanSelector, Nav +
 * NavItem, UserMenu) vive en su propio fichero — SidebarHeader.tsx,
 * SidebarPlanSelector.tsx, SidebarNav.tsx/SidebarNavItem.tsx,
 * SidebarUserMenu.tsx — mismo patrón compuesto que Card
 * (CardHeader/CardContent/CardFooter). Antes estas piezas vivían inline aquí
 * mismo: se separan para que cada una se pueda documentar, testear o llevar a
 * producción por separado, en vez de copiar un único bloque opaco.
 *
 * Estado que reproduce el real (antes simplificado, ahora fiel):
 * - Al entrar no hay plan elegido todavía (real: `ViewType.RESUMEN`, sin
 *   `activePlan`) y se está en el resumen de planes: el selector de arriba dice «Resumen de planes», no
 *   un plan puesto por defecto (y «Seleccionar plan» solo queda para cuando no hay nada). Elegir un plan
 *   es cosmético, como en el real, y «Resumen de planes» es la primera fila del desplegable.
 * - Con un solo plan no hay resumen, pero sí desplegable: el plan, marcado, y el botón «Añadir plan». Con
 *   varios, el botón va al final, debajo de los planes.
 * - El menú de usuario (abajo) tiene el switch Personal/Admin real. Cambiar a Admin cambia el selector
 *   de arriba por el de EMPRESAS (`SidebarCompanySelector`), con «Admin» al lado de cada nombre: un
 *   desplegable con varias SL y, con una sola, el campo sin desplegable — volver a Personal recupera el
 *   plan que estuviera elegido.
 */

interface SidebarPropsBase {
	activeRoute?: string;
	/** Sin valor por defecto a propósito: un nombre inventado dentro del bloque
	 *  acaba en producción en cuanto alguien lo instale y no lo pase. */
	userName: string;
	userEmail: string;
	/** Planes del usuario. Los de Pendiente KYC se listan en Resumen pero no son
	 *  seleccionables, igual que en el real (findActiveEmpleoPlan prioriza el EMPLEO activo). */
	plans?: PlanSummary[];
	/** Empresas que administra, si tiene acceso de administración. */
	adminAccess?: AdminAccessSummary | null;
	/**
	 * Las secciones del menú. Sin esto, el bloque usa su lista de ejemplo.
	 *
	 * 🔴 Hace falta porque esa lista es una ficción de Storybook, y el propio
	 * `sidebarNavItems.tsx` lo dice: «en el código real "Plan de Empleo" y "Simuladores" no
	 * salen NUNCA juntos». En la aplicación las secciones cambian según el rol y el tipo de
	 * plan, así que un bloque que no las acepte por props no se puede usar fuera de las
	 * historias.
	 */
	navItems?: NavItemData[];
	/** Al pulsar una sección, con su ruta: quien monta el bloque es quien navega. */
	onNavigate?: (route: string) => void;
	/** Configuración, Soporte y Cerrar sesión del menú de usuario (ver `AccionesDeCuenta`). */
	cuenta?: AccionesDeCuenta;
	/**
	 * Enseñar el cambio Personal / Admin. Con empresas administradas sale por defecto; quien monta el
	 * bloque lo apaga cuando no hay vista personal a la que volver (un admin sin planes propios).
	 */
	showPersonalAdminSwitch?: boolean;
	/** Los textos que se ven, si no son los de por defecto (ver `navTextos.ts`). */
	textos?: Partial<TextosDeNavegacion>;
}

export type SidebarProps = SidebarPropsBase & ControlDeNavegacion & ControlDelMenu;

export const Sidebar = ({
	activeRoute = 'my-benefit',
	userName,
	userEmail,
	plans = [],
	adminAccess = null,
	navItems,
	onNavigate,
	cuenta,
	showPersonalAdminSwitch,
	textos: textosPropios,
	isMenuOpen: menuConducido,
	onToggleMenu,
	activePlanId,
	onSelectPlan,
	isSummaryActive,
	onSelectSummary,
	onAddPlan,
	activeCompanyId,
	onSelectCompany,
	isAdminActive,
	onSelectPersonal,
	onSelectAdmin
}: SidebarProps) => {
	const selectablePlans = plans.filter((plan) => plan.status !== PlanStatus.PENDING_KYC);
	const textos = conTextos(textosPropios);
	// Plegar y desplegar: lo lleva la barra salvo que quien la monta lo conduzca (`ControlDelMenu`).
	const [menuPropio, setMenuPropio] = useState(true);
	const isMenuOpen = onToggleMenu ? menuConducido : menuPropio;
	const plegarODesplegar = onToggleMenu ?? (() => setMenuPropio((v) => !v));

	// Estado de reserva, para cuando el bloque NO viene conducido. Al entrar no hay plan elegido, igual
	// que el real (ViewType.RESUMEN), y se está en el resumen de planes: el botón dice «Resumen de
	// planes», no «Seleccionar plan».
	const [planPropio, setPlanPropio] = useState<string | undefined>(undefined);
	const [resumenPropio, setResumenPropio] = useState(true);
	const [empresaPropia, setEmpresaPropia] = useState<string | undefined>(undefined);
	const [adminPropio, setAdminPropio] = useState(false);

	// El tipo garantiza que el par viene entero o no viene, así que basta mirar una de las dos.
	const planElegido = onSelectPlan ? activePlanId : planPropio;
	const elegirPlan = (plan: PlanSummary) => {
		if (onSelectPlan) return onSelectPlan(plan);
		setPlanPropio(plan.id);
		setResumenPropio(false);
	};
	// Conducido, sin `onSelectSummary` no hay a dónde ir y la fila no se enseña.
	const enResumen = onSelectPlan ? (isSummaryActive ?? false) : resumenPropio;
	const irAlResumen = onSelectPlan
		? onSelectSummary
		: () => {
				setPlanPropio(undefined);
				setResumenPropio(true);
			};

	// Añadir plan: sin control desde fuera no hay a dónde ir, pero el botón se enseña igualmente (es el
	// caso de las stories); conducido, sin `onAddPlan` no se enseña.
	const anadirPlan = onSelectPlan ? onAddPlan : () => {};

	const empresaElegida = onSelectCompany ? activeCompanyId : empresaPropia;
	const elegirEmpresa = (company: AdminCompanySummary) =>
		onSelectCompany ? onSelectCompany(company) : setEmpresaPropia(company.id);

	const enAdmin = onSelectAdmin ? isAdminActive : adminPropio;
	const irAPersonal = onSelectPersonal ?? (() => setAdminPropio(false));
	const irAAdmin = onSelectAdmin ?? (() => setAdminPropio(true));

	// En Admin, el selector de empresas ocupa el sitio del de planes: con varias SL es un desplegable y con
	// una sola se enseña el campo igualmente, sin desplegable (no hay nada que elegir).
	const hasCompanies = (adminAccess?.companies.length ?? 0) > 0;
	const showsTopSelector = !enAdmin || hasCompanies;

	return (
		<TooltipProvider delayDuration={0}>
			{/* dark:border-sidebar-border en el borde derecho del <aside>: vive sobre
			    --sidebar-background, no sobre --card — reusar --border (calibrado para
			    --card) se veía como una línea demasiado blanca contra un fondo tan
			    oscuro. Mismo criterio que SidebarPlanSelector.tsx/SidebarUserMenu.tsx. */}
			<aside
				className={`relative h-dvh bg-white border-r border-gray-200 dark:bg-sidebar dark:border-sidebar-border flex flex-col transition-[width] duration-300 ease-in-out ${
					isMenuOpen ? 'w-[300px]' : 'w-[80px]'
				}`}
			>
				<SidebarHeader isMenuOpen={isMenuOpen} onToggle={plegarODesplegar} textos={textos} />

				{/* Sin planes no hay nada que elegir ni resumen al que ir: sin selector (los planes aún no han
				    llegado, o no tiene ninguno). Salvo que quien lo monta pase «Añadir plan»: para quien no
				    tiene ninguno, es justo la acción que tiene sentido. */}
				{showsTopSelector && !enAdmin && (selectablePlans.length > 0 || Boolean(onAddPlan)) && (
					<SidebarPlanSelector
						plans={selectablePlans}
						activePlanId={planElegido}
						onSelectPlan={elegirPlan}
						isSummaryActive={enResumen}
						onSelectSummary={irAlResumen}
						onAddPlan={anadirPlan}
						isMenuOpen={isMenuOpen}
						textos={textos}
					/>
				)}
				{enAdmin && hasCompanies && adminAccess && (
					<SidebarCompanySelector
						companies={adminAccess.companies}
						activeCompanyId={empresaElegida}
						onSelectCompany={elegirEmpresa}
						isMenuOpen={isMenuOpen}
						textos={textos}
					/>
				)}

				<SidebarNav
					items={navItems}
					activeRoute={activeRoute}
					isMenuOpen={isMenuOpen}
					onNavigate={onNavigate}
				/>
				<SidebarUserMenu
					userName={userName}
					userEmail={userEmail}
					isMenuOpen={isMenuOpen}
					isAdminActive={enAdmin}
					adminAccess={adminAccess}
					onSelectPersonal={irAPersonal}
					onSelectAdmin={irAAdmin}
					showPersonalAdminSwitch={showPersonalAdminSwitch}
					cuenta={cuenta}
					textos={textos}
				/>
			</aside>
		</TooltipProvider>
	);
};
